import { cn } from '@/lib/utils';

/**
 * Minimal job-site horizon for the sign-in page: one hairline drawing in `currentColor` at low
 * opacity, alive but quiet. A tower crane whose trolley travels the jib while the hook lowers a load
 * and lifts it again; the outline of a structure whose floors draw themselves in one after another
 * as it goes up; a mast broadcasting, with a dashed line of messages flowing from it toward the
 * crew; a pickup that crosses the site now and then; two blinking beacons. No fills, no clutter.
 *
 * Motion is opt-in through `.anim-*` classes that only run under `prefers-reduced-motion:
 * no-preference` (see index.css). Decorative, always `aria-hidden`.
 *
 * `fit="cover"` crops the sides on narrow screens (phones); `fit="contain"` letterboxes on wide ones so
 * the crane top and the signal are never cut off.
 */
export function JobSiteSkyline({ className, fit = 'contain' }: { className?: string; fit?: 'cover' | 'contain' }) {
  return (
    <svg
      viewBox="0 0 1440 160"
      preserveAspectRatio={fit === 'cover' ? 'xMidYMax slice' : 'xMidYMax meet'}
      aria-hidden
      className={cn('block h-full w-full', className)}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="square"
    >
      {/* Ground */}
      <line x1="0" y1="159.5" x2="1440" y2="159.5" opacity="0.25" />

      {/* Site office and a container */}
      <g opacity="0.22">
        <rect x="120.5" y="126.5" width="76" height="33" />
        <line x1="120.5" y1="138.5" x2="196.5" y2="138.5" />
        <rect x="206.5" y="140.5" width="46" height="19" />
      </g>

      {/* Tower crane */}
      <g opacity="0.3">
        <line x1="430" y1="159.5" x2="430" y2="26" strokeWidth="2" />
        <line x1="300" y1="26.5" x2="760" y2="26.5" strokeWidth="1.5" />
        <line x1="430" y1="8" x2="430" y2="26" />
        <line x1="430" y1="8" x2="756" y2="26" opacity="0.6" />
        <line x1="430" y1="8" x2="304" y2="26" opacity="0.6" />
        <rect x="300.5" y="26.5" width="40" height="8" />
        <rect x="418.5" y="30.5" width="24" height="12" />
      </g>
      {/* Trolley travels the jib; the cable pays out and the load comes down as it goes. */}
      <g className="anim-trolley" opacity="0.3">
        <rect x="514.5" y="27.5" width="11" height="4" />
        <line x1="520" y1="31" x2="520" y2="96" className="anim-hook-cable" />
        <g className="anim-hook-load">
          <line x1="514" y1="96" x2="526" y2="96" />
          <rect x="509.5" y="96.5" width="21" height="8" />
        </g>
      </g>
      <rect x="428" y="2" width="4" height="4" stroke="none" className="fill-primary dark:fill-brand-accent anim-blink" />

      {/* Structure going up: walls stand, floors draw in one after another. */}
      <g opacity="0.22">
        <line x1="620.5" y1="159.5" x2="620.5" y2="92.5" />
        <line x1="790.5" y1="159.5" x2="790.5" y2="92.5" />
        <line x1="705.5" y1="159.5" x2="705.5" y2="92.5" opacity="0.7" />
        <line x1="620.5" y1="136.5" x2="790.5" y2="136.5" className="anim-draw" />
        <line x1="620.5" y1="114.5" x2="790.5" y2="114.5" className="anim-draw" style={{ animationDelay: '3s' }} />
        <line x1="620.5" y1="92.5" x2="790.5" y2="92.5" className="anim-draw" style={{ animationDelay: '6s' }} />
      </g>

      {/* Mast with signal, and the messages flowing out toward the crew */}
      <g opacity="0.3">
        <line x1="1180" y1="159.5" x2="1180" y2="70" />
        <line x1="1162" y1="159.5" x2="1180" y2="112" opacity="0.6" />
        <line x1="1198" y1="159.5" x2="1180" y2="112" opacity="0.6" />
      </g>
      <rect x="1178" y="64" width="4" height="4" stroke="none" className="fill-primary dark:fill-brand-accent anim-blink" style={{ animationDelay: '1.3s' }} />
      <g className="stroke-primary dark:stroke-brand-accent">
        <path d="M1170 60 A 10 10 0 0 1 1190 60" className="anim-signal" />
        <path d="M1162 60 A 18 18 0 0 1 1198 60" className="anim-signal" style={{ animationDelay: '0.6s' }} />
        <path d="M1166 72 Q 990 18 820 76" strokeDasharray="3 7" strokeLinecap="round" className="anim-flow" opacity="0.45" />
      </g>

      {/* A pickup crosses the site now and then. */}
      <g className="anim-truck" opacity="0.3">
        <path d="M1440 159.5 v-9 h6 l5 -7 h13 l4 7 h12 v9" />
        <circle cx="1449" cy="159.5" r="3" />
        <circle cx="1471" cy="159.5" r="3" />
      </g>
    </svg>
  );
}
